/**
 * StewardBackboneCard — "you already told me about this; shall I write it down?"
 *
 * THE COMPLAINT THIS ANSWERS. Wade described his cruise in the trip description
 * — ship, line, both ports, both dates, the shoulder nights — and the itinerary
 * showed flights and then a seven-night hole. Steward *knew*; she had simply
 * never offered to put it on a day. Worse, when the offer finally existed it had
 * to be IMPOSSIBLE TO MISS: the first version of this feature was invisible, and
 * "it's in there somewhere" is the same as not shipping it.
 *
 * So this card sits directly under the gap banner, carries a headline that names
 * the thing ("Your cruise isn't on the itinerary yet") rather than a generic
 * count, and shows each proposed row with the disclosure it came from.
 *
 * PROPOSES, NEVER AUTO-WRITES. Every Add calls itineraryApi.createItem — the
 * same write path as the manual dialog and StewardCard. The traveler's tap is
 * the only thing that ever writes a row. Accept-all is a loop over that same
 * call, not a bulk endpoint, so a partial failure leaves the successful rows in
 * place and re-offers only what's left on the next load.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Plus, Ship } from "lucide-react";
import { itineraryApi, stewardApi } from "@/lib/api";
import { ITEM_ICON, ITEM_LABEL } from "@/components/trips/itinerary/itinerary-utils";
import type { BackboneProposal } from "@/lib/types";
import {
  addAllLabel,
  backboneHeadline,
  provenanceLine,
  shortDay,
  spanLabel,
} from "./stewardBackbone-utils";
import { invalidateAfterItineraryWrite } from "@/lib/itineraryCache";

interface RowProps {
  proposal: BackboneProposal;
  added: boolean;
  busy: boolean;
  failed: boolean;
  onAdd: () => void;
}

export function BackbonePropRow({ proposal, added, busy, failed, onAdd }: RowProps) {
  const Icon = ITEM_ICON[proposal.item.item_type];
  const span = spanLabel(proposal.spanDays);

  return (
    <li className="flex items-start gap-3 rounded-lg border border-border bg-card p-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-semibold text-foreground">{proposal.item.title}</span>
          <span className="text-xs font-medium text-[hsl(var(--primary))]">
            {shortDay(proposal.item.day_date)}
            {span ? ` · ${span}` : ""}
          </span>
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {ITEM_LABEL[proposal.item.item_type]}
          </span>
        </div>
        <p className="text-xs leading-snug text-muted-foreground">{proposal.why}</p>
        {proposal.item.notes && (
          <p className="text-xs leading-snug text-muted-foreground/80">{proposal.item.notes}</p>
        )}
        {failed && (
          <p className="text-xs text-destructive">Couldn't add that one — try again.</p>
        )}
      </div>
      <button
        type="button"
        onClick={onAdd}
        disabled={added || busy}
        aria-label={
          added
            ? `${proposal.item.title} added to itinerary`
            : `Add ${proposal.item.title} to itinerary`
        }
        className={
          "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors motion-reduce:transition-none " +
          (added
            ? "cursor-default bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200"
            : "bg-[hsl(var(--foreground))] text-white hover:opacity-90 disabled:opacity-60")
        }
      >
        {busy ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            Adding
          </>
        ) : added ? (
          <>
            <Check className="h-3.5 w-3.5" aria-hidden="true" /> Added
          </>
        ) : (
          <>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add
          </>
        )}
      </button>
    </li>
  );
}

interface ViewProps {
  proposals: BackboneProposal[];
  addedKeys: string[];
  busyKeys: string[];
  failedKeys: string[];
  onAdd: (proposal: BackboneProposal) => void;
  onAddAll: () => void;
}

/** Presentational half — no data fetching, so the copy is testable in isolation. */
export function StewardBackboneCardView({
  proposals,
  addedKeys,
  busyKeys,
  failedKeys,
  onAdd,
  onAddAll,
}: ViewProps) {
  if (proposals.length === 0) return null;
  const remaining = proposals.filter((p) => !addedKeys.includes(p.key));
  const anyBusy = busyKeys.length > 0;

  return (
    <section
      aria-labelledby="steward-backbone-heading"
      data-testid="steward-backbone"
      className="rounded-xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/[0.06] p-4"
    >
      <div className="flex items-start gap-3">
        <Ship className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--primary))]" aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-3">
          <div className="space-y-1">
            <p className="text-[11px] font-medium uppercase tracking-wide text-[hsl(var(--primary))]">
              Your Steward · from what you've told me
            </p>
            <h2
              id="steward-backbone-heading"
              className="text-sm font-semibold text-foreground"
            >
              {backboneHeadline(proposals)}
            </h2>
            <p className="text-xs leading-snug text-muted-foreground">
              {provenanceLine(proposals)}
            </p>
          </div>

          <ul className="space-y-2">
            {proposals.map((p) => (
              <BackbonePropRow
                key={p.key}
                proposal={p}
                added={addedKeys.includes(p.key)}
                busy={busyKeys.includes(p.key)}
                failed={failedKeys.includes(p.key)}
                onAdd={() => onAdd(p)}
              />
            ))}
          </ul>

          {remaining.length > 1 && (
            <button
              type="button"
              onClick={onAddAll}
              disabled={anyBusy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--foreground))] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60 motion-reduce:transition-none"
            >
              {anyBusy ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  Adding
                </>
              ) : (
                <>
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  {addAllLabel(remaining.length)} to the itinerary
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

/** Container: fetches the proposals and owns the add mutation + invalidation. */
export default function StewardBackboneCard({ tripId }: { tripId: string }) {
  const queryClient = useQueryClient();
  const [addedKeys, setAddedKeys] = useState<string[]>([]);
  const [busyKeys, setBusyKeys] = useState<string[]>([]);
  const [failedKeys, setFailedKeys] = useState<string[]>([]);

  const { data } = useQuery({
    queryKey: ["stewardBackbone", tripId],
    queryFn: () => stewardApi.backbone(tripId),
    // The card is a nicety — never retry it into a spinner the traveler watches.
    retry: false,
    staleTime: 60_000,
  });

  const add = useMutation({
    mutationFn: async (proposals: BackboneProposal[]) => {
      const done: string[] = [];
      const failed: string[] = [];
      // Serial on purpose: the itinerary's sort_order is derived server-side and
      // three parallel inserts would race for the same day's ordering.
      for (const p of proposals) {
        try {
          await itineraryApi.createItem(tripId, {
            item_type: p.item.item_type,
            title: p.item.title,
            city: p.item.city,
            day_date: p.item.day_date,
            notes: p.item.notes,
            is_anchor: p.item.is_anchor,
          });
          done.push(p.key);
        } catch {
          failed.push(p.key);
        }
      }
      return { done, failed };
    },
    onSuccess: ({ done, failed }) => {
      setAddedKeys((k) => [...new Set([...k, ...done])]);
      setFailedKeys(failed);
      // Everything that derives from the itinerary, including the gap banner —
      // a day that now has a plan is a day the banner must stop counting.
      invalidateAfterItineraryWrite(queryClient, tripId);
    },
    onSettled: () => setBusyKeys([]),
  });

  const run = (proposals: BackboneProposal[]) => {
    if (add.isPending || proposals.length === 0) return;
    setFailedKeys([]);
    setBusyKeys(proposals.map((p) => p.key));
    add.mutate(proposals);
  };

  const proposals = data?.proposals ?? [];
  if (proposals.length === 0) return null;

  return (
    <StewardBackboneCardView
      proposals={proposals}
      addedKeys={addedKeys}
      busyKeys={busyKeys}
      failedKeys={failedKeys}
      onAdd={(p) => run([p])}
      onAddAll={() => run(proposals.filter((p) => !addedKeys.includes(p.key)))}
    />
  );
}
